/**
 * Thin API client. Session state lives in the httpOnly `calm_ops.sid` cookie,
 * so `credentials: 'include'` is used on every same-origin call and there is no
 * token in localStorage or sessionStorage to steal.
 */
export const PILLAR_CODES = ['CGS', 'CLM', 'CAC'] as const;
export type PillarCode = (typeof PILLAR_CODES)[number];

/**
 * §A.2.2 / §A.2.4 vocabularies, mirrored for the create-project form. The
 * server validates against the Postgres enums regardless of what is sent, so
 * these lists decide what the form offers, never what the database accepts.
 */
export const WORKSTREAM_CODES = [
  'RES-MIT',
  'RES-REM',
  'RES-NEW',
  'COM-MIT',
  'COM-REM',
  'COM-NEW',
  'FED-PROG',
  'MIL-INST',
  'STATE-PROC',
] as const;
export type WorkstreamCode = (typeof WORKSTREAM_CODES)[number];

/** The labels read as English rather than as column values. */
export const WORKSTREAM_LABELS: Record<WorkstreamCode, string> = {
  'RES-MIT': 'Residential \u2014 mitigation',
  'RES-REM': 'Residential \u2014 remediation',
  'RES-NEW': 'Residential \u2014 new construction',
  'COM-MIT': 'Commercial \u2014 mitigation',
  'COM-REM': 'Commercial \u2014 remediation',
  'COM-NEW': 'Commercial \u2014 new construction',
  'FED-PROG': 'Federal program',
  'MIL-INST': 'Military installation',
  'STATE-PROC': 'State procurement',
};

export const CLIENT_ACCOUNT_TYPES = [
  'municipal',
  'state',
  'federal',
  'military',
  'carrier',
  'commercial',
  'residential',
  'ngo',
  'other',
] as const;
export type ClientAccountType = (typeof CLIENT_ACCOUNT_TYPES)[number];

/**
 * The statuses a project may be *created* into. `on_hold` is absent because the
 * server rejects it: a hold carries a start date and a cause that §4.5 writes on
 * transition, and a project that begins on hold has no transition to write them.
 * The later lifecycle stages are absent because a project is not created into
 * its own closeout.
 */
export const CREATABLE_STATUSES = [
  'lead_opportunity',
  'estimating_bidding',
  'submitted_pending_award',
  'access_clearance_pending',
  'contracting_mobilization',
  'active_fieldwork',
] as const;
export type CreatableStatus = (typeof CREATABLE_STATUSES)[number];

export interface ClientAccountOption {
  id: string;
  name: string;
  accountType: string;
  pillars: PillarCode[];
}

export interface CreateProjectBody {
  code: string;
  name: string;
  pillar: PillarCode;
  workstream: WorkstreamCode;
  status?: CreatableStatus;
  clientAccountId?: string;
  newClientAccount?: { name: string; accountType: ClientAccountType };
  siteCity?: string;
  siteState?: string;
}

export interface SessionUser {
  id: string;
  email: string;
  fullName: string;
  role: string;
  pillars: PillarCode[];
  domains: string[];
  locale: string;
}

/**
 * Field names are the Appendix A column names as returned by the API. They are
 * deliberately NOT camel-cased on the way out: renaming a spine column between
 * the database and the UI is how a schema and a screen quietly diverge.
 */
export interface Project {
  id: string;
  code: string;
  name: string;
  pillar: PillarCode;
  workstream: string | null;
  status: string;
  percent_complete: string | number | null;
  site_city: string | null;
  site_state: string | null;
  /**
   * P4-8. Present only on the single-project GET (`/projects/:id`), which
   * selects these columns explicitly — the list route (`/projects`) does not,
   * so a row from `api.projects()` carries these as `undefined`, not `null`.
   */
  site_postal_code?: string | null;
  site_lat?: string | number | null;
  site_lon?: string | number | null;
  weather_group_key?: string | null;
  updated_at: string;
}

/** Body for `PATCH /projects/:id/site`. `siteLat`/`siteLon` travel together (projects_geo_pair) or not at all. */
export interface UpdateProjectSiteBody {
  siteAddress?: string;
  siteCity?: string;
  siteState?: string;
  sitePostalCode?: string;
  siteLat?: number;
  siteLon?: number;
}

/** The row `PATCH /projects/:id/site` returns — only the site/weather columns, not the full Project. */
export interface ProjectSiteRow {
  id: string;
  site_address: string | null;
  site_city: string | null;
  site_state: string | null;
  site_postal_code: string | null;
  site_lat: string | number | null;
  site_lon: string | number | null;
  weather_group_key: string | null;
}

/**
 * P4-8. `GET /projects/:id/weather` — a pure cache read, never an ad-hoc NWS
 * call (§6.6). Three states: no coordinates saved yet, coordinates saved but
 * the scheduler hasn't cached this cell yet, and a cached (possibly stale) reading.
 */
export type ProjectWeather =
  | { state: 'no_site_coordinates' }
  | { state: 'not_cached_yet'; groupKey: string }
  | {
      state: 'ok';
      groupKey: string;
      stale: boolean;
      fetchedAt: string;
      expiresAt: string;
      current: { summary: string; tempF: number; [key: string]: unknown };
      periods: unknown[];
      alerts: unknown[];
    };

/**
 * Phase 2 — contacts. Field names are the Appendix A column names as returned
 * by the API, for the same reason Project's are: a rename between the database
 * and the screen is how a schema and a UI quietly diverge.
 */
export interface ContactRow {
  id: string;
  display_name: string;
  company_name: string | null;
  category: string;
  tier: 'A' | 'B' | 'C' | 'D';
  email: string | null;
  phone_primary: string | null;
  city: string | null;
  state_region: string | null;
  pillars: PillarCode[];
  is_active: boolean;
  legacy_jnid: string | null;
  updated_at: string;
  project_count: number;
  in_review: boolean;
}

export interface ContactPage {
  contacts: ContactRow[];
  page: number;
  pageSize: number;
  total: number;
  hasMore: boolean;
  scope: PillarCode[];
  /** Rows no session can see because they carry no pillar. Surfaced, not hidden. */
  pillarlessHidden: number;
}

/** P4-2. The four project-independent links a pair of contacts can carry. */
export type ContactRelationshipKind =
  | 'household'
  | 'same_company'
  | 'referred_by'
  | 'related_party';

/**
 * One relationship as the contact currently open reads it: the OTHER person is
 * named, because the row is stored once for the pair and whichever contact you
 * opened is not the interesting half.
 */
export interface ContactRelationship {
  id: string;
  kind: ContactRelationshipKind;
  directed: boolean;
  referrer_id: string | null;
  evidence: string | null;
  note: string | null;
  created_at: string;
  this_side: 'a' | 'b';
  other_id: string;
  other_display_name: string;
  other_company_name: string | null;
  other_pillars: PillarCode[];
}

export interface ContactDetail extends ContactRow {
  first_name: string | null;
  last_name: string | null;
  email_secondary: string | null;
  phone_secondary: string | null;
  website: string | null;
  address_line1: string | null;
  address_line2: string | null;
  postal_code: string | null;
  country: string | null;
  source_channel: string | null;
  source_detail: string | null;
  notes: string | null;
  dedupe_key: string | null;
  merged_into_id: string | null;
  import_batch_id: string | null;
  legacy_payload: Record<string, unknown> | null;
}

export interface ReviewSide {
  id: string | null;
  name: string | null;
  company: string | null;
  email: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  pillars: PillarCode[] | null;
  tier: string | null;
  category: string | null;
  address: string | null;
  legacy: string | null;
}

export interface ReviewEntry {
  id: string;
  kind: string;
  status: 'open' | 'held' | 'merged' | 'rejected' | 'resolved';
  decision_key: string;
  decision_ref: string | null;
  legacy_rows: string | null;
  signal: string;
  recommendation: string | null;
  binding_spec: string | null;
  blocked_reason: string | null;
  resolution_note: string | null;
  resolved_at: string | null;
  resolved_by_name: string | null;
  created_at: string;
  a: ReviewSide;
  b: ReviewSide;
}

export interface ReviewPage {
  entries: ReviewEntry[];
  page: number;
  pageSize: number;
  /** Rows matching the CURRENT filters, including the status filter. */
  total: number;
  /**
   * Rows matching every filter EXCEPT status — the true All total, whichever tab
   * is selected. `total` is the current tab's count and is not interchangeable
   * with it: with 0 open, 5 held and 3 merged, `total` on the Open tab is 0.
   */
  totalAllStatuses: number;
  hasMore: boolean;
  countsByStatus: Record<string, number>;
}

export interface MergeRow {
  id: string;
  winner_id: string;
  loser_id: string | null;
  loser_legacy_jnid: string;
  origin: 'workbook_prefold' | 'reviewed';
  reason: string;
  performed_at: string;
  reverted_at: string | null;
  revert_reason: string | null;
  winner_name: string;
  loser_name: string | null;
  performed_by_name: string | null;
}

export interface ContactFilters {
  page?: number;
  q?: string;
  tier?: string;
  category?: string;
}

/** The API flattens both sides of a review entry; this nests them for display. */
function nestReviewSides(raw: Record<string, unknown>): ReviewEntry {
  const side = (prefix: 'a' | 'b'): ReviewSide => ({
    id: (raw[`${prefix}_id`] as string | null) ?? null,
    name: (raw[`${prefix}_name`] as string | null) ?? null,
    company: (raw[`${prefix}_company`] as string | null) ?? null,
    email: (raw[`${prefix}_email`] as string | null) ?? null,
    phone: (raw[`${prefix}_phone`] as string | null) ?? null,
    city: (raw[`${prefix}_city`] as string | null) ?? null,
    state: (raw[`${prefix}_state`] as string | null) ?? null,
    pillars: (raw[`${prefix}_pillars`] as PillarCode[] | null) ?? null,
    tier: (raw[`${prefix}_tier`] as string | null) ?? null,
    category: (raw[`${prefix}_category`] as string | null) ?? null,
    address: (raw[`${prefix}_address`] as string | null) ?? null,
    legacy: (raw[`${prefix}_legacy`] as string | null) ?? null,
  });
  return { ...(raw as unknown as ReviewEntry), a: side('a'), b: side('b') };
}

/* -------------------------------- Phase 3 deliverables 15 and 16 -------- */

/**
 * §1.2.1. `contractValue` is `null`, never 0, whenever the segment's projects
 * do not all carry a `project_financials` row. `financialRows` says how many
 * did, so the interface can explain the blank instead of leaving one.
 */
export interface HealthSegment {
  status: string;
  count: number;
  contractValue: number | null;
  financialRows: number;
  atRisk: number;
  isDefault: boolean;
}

export interface OperationalHealth {
  asOf: string;
  scope: PillarCode[];
  segments: HealthSegment[];
  totalProjects: number;
  totalAtRisk: number;
  defaultSegment: string;
  money: {
    available: boolean;
    reason: string | null;
    projectsWithFinancials: number;
    projectsTotal: number;
  };
  flagsWithoutAColumn: string[];
}

/** §1.3. Copy and identity colour come from the server so they cannot drift. */
export interface PillarButton {
  code: PillarCode;
  displayName: string;
  legalName: string;
  microCopy: string;
  secondaryLine: string;
  identityColour: string;
  identityOn: 'navy' | 'own';
  motto: string | null;
  granted: boolean;
  projectCount: number;
  activeCount: number;
  pipelineCount: number;
  redFlagCount: number;
  activeContractValue: number | null;
  activeContractValueReason: string | null;
}

export interface WorldProjectRow {
  id: string;
  code: string;
  name: string;
  status: string;
  workstream: string;
  clientAccountName: string;
  percentComplete: number;
  siteCity: string | null;
  siteState: string | null;
  pmName: string | null;
  flags: string[];
  contractValue: number | null;
}

export interface WorldStatusGroup {
  status: string;
  count: number;
  expandedByDefault: boolean;
  projects: WorldProjectRow[];
}

export interface PhotoThumb {
  id: string;
  title: string;
  capturedAt: string | null;
  dropboxPath: string;
}

export interface DailyLogEntry {
  id: string;
  logDate: string;
  projectId: string;
  projectCode: string;
  authorName: string | null;
  crewCount: number | null;
  hoursWorked: number | null;
  weather: { summary?: string; tempF?: number; [key: string]: unknown } | null;
  workPerformed: string;
  blockers: string | null;
  photos: PhotoThumb[];
  photoTotal: number;
}

export interface DailyLogFeed {
  logs: DailyLogEntry[];
  days: number;
  total: number;
}

export interface ProcurementItem {
  id: string;
  projectId: string;
  projectCode: string;
  description: string;
  quantity: number;
  unit: string | null;
  leadTimeDays: number;
  neededOnSite: string | null;
  orderByDate: string | null;
  daysUntilOrderBy: number | null;
  status: string;
  supplierName: string | null;
  poNumber: string | null;
  orderByPassed: boolean;
}

export interface ProcurementFeed {
  items: ProcurementItem[];
  atRisk: number;
  total: number;
}

export interface CompanyWorld {
  scope: PillarCode[];
  statusGroups: WorldStatusGroup[];
  dailyLogs: DailyLogFeed;
  procurement: ProcurementFeed;
  mediaInbox: { pending: number; reason: string | null };
}

/* ------------------------------------ deliverable 13: gates and the hold -- */

/** One §4.5 gate as the server reports it. `detail` is the evidence read. */
export interface GateView {
  id: string;
  kind: 'exit' | 'entry';
  guards: string;
  outcome: 'satisfied' | 'blocked' | 'not_evaluable' | 'advisory';
  detail: string;
  overridable: boolean;
  specGap?: string;
}

/**
 * The hold as the project row records it. `startedOn` is written server-side
 * on entry to `on_hold` and is read-only here — the client never sends it.
 */
export interface HoldState {
  cause: string | null;
  startedOn: string | null;
  resumeOn: string | null;
}

export interface ReachableStatus {
  status: string;
  allowed: boolean;
  overrideCapable: boolean;
  causeRequired: boolean;
  blocking: string[];
  advisories: string[];
}

export interface ProjectStatusView {
  project: { id: string; code: string; status: string };
  workstream: string;
  hold: HoldState;
  exitGates: GateView[];
  blocking: string[];
  notEvaluable: string[];
  advisories: GateView[];
  reachable: ReachableStatus[];
}

export interface ApiError {
  error: string;
  message: string;
  requiredPillar?: PillarCode;
  grantedPillars?: PillarCode[];
}

export class ApiFailure extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiError,
  ) {
    super(body.message || body.error);
    this.name = 'ApiFailure';
  }
}

/**
 * Build-time API base. Empty in the single-service production layout, so every
 * request stays a relative, first-party /api path exactly as before. A hosted
 * preview serves the client from another origin and sets VITE_API_BASE to the
 * proxied API origin. Never guessed at runtime.
 */
const API_BASE = import.meta.env.VITE_API_BASE ?? '';

/**
 * PREVIEW ONLY. The hosted preview proxy strips Set-Cookie, so no cookie
 * session survives it. When the server is running in preview mode it returns
 * the signed session id in X-Preview-Session; we send it back on the next
 * request.
 *
 * The value is held in a module-scoped variable and NOWHERE ELSE. It is never
 * written to localStorage or sessionStorage. Two reasons, both load-bearing:
 *
 *   1. The preview runs inside a sandboxed frame whose origin is the opaque
 *      literal "null". Both Storage APIs throw SecurityError there, so a
 *      storage-backed token cannot work in the one place it is needed.
 *   2. A variable in module scope dies with the page. It does not survive a
 *      refresh, is not shared between tabs, and is not reachable through
 *      window, document or any Storage API that injected script can enumerate.
 *
 * Cost of (2), stated plainly: refreshing the preview signs you out and you log
 * in again. That is the intended trade.
 *
 * In production API_BASE is empty, the server never sends this header, this
 * variable stays null, and every request is the cookie-only call it always was.
 */
const PREVIEW_SESSION_HEADER = 'X-Preview-Session';

/** In memory for the life of the page. Never persisted. */
let carriedSession: string | null = null;

function readCarriedSession(): string | null {
  return carriedSession;
}

function storeCarriedSession(value: string): void {
  carriedSession = value;
}

/**
 * Cookies go out only when the API is same-origin — production and local dev,
 * where API_BASE is '' and /api is a first-party path. The hosted preview sets
 * API_BASE to another origin and runs in an opaque-origin frame, where the
 * browser refuses to dispatch a credentialed request at all: fetch throws
 * "Failed to fetch" before anything leaves the machine, which the UI could only
 * report as a rejected password. There the session rides the header instead.
 */
const CROSS_ORIGIN_API = API_BASE !== '';
const CREDENTIALS_MODE: RequestCredentials = CROSS_ORIGIN_API ? 'omit' : 'include';

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const carried = readCarriedSession();
  const response = await fetch(`${API_BASE}/api${path}`, {
    ...init,
    credentials: CREDENTIALS_MODE,
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(carried ? { [PREVIEW_SESSION_HEADER]: carried } : {}),
      ...init.headers,
    },
  });

  const issued = response.headers.get(PREVIEW_SESSION_HEADER);
  if (issued) storeCarriedSession(issued);

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const body = text ? (JSON.parse(text) as unknown) : {};

  if (!response.ok) {
    throw new ApiFailure(response.status, body as ApiError);
  }
  return body as T;
}

/**
 * The scheduling engine (P4-6, §4.4). A pending reminder row as the upcoming
 * list reads it — one per ladder rung, so a single deadline appears several
 * times at different lead times, which is the point.
 */
export interface UpcomingReminder {
  id: string;
  projectId: string | null;
  projectName: string | null;
  pillar: PillarCode | null;
  entityType: string;
  entityId: string | null;
  eventClass: string | null;
  variant: string;
  label: string | null;
  title: string;
  body: string | null;
  dueAt: string;
  fireAt: string;
  offsetMinutes: number | null;
  recipientCount: number;
}

/** A record whose calendar push exhausted its five attempts (§4.4). */
export interface CalendarSyncFailure {
  entityType: string;
  entityId: string;
  projectId: string | null;
  eventClass: string | null;
  label: string | null;
  attempts: number;
  lastError: string | null;
  lastAttemptAt: string | null;
}

export interface LadderRung {
  id: string;
  eventClass: string;
  variant: string;
  surface: 'calendar' | 'engine';
  method: 'email' | 'popup' | 'push';
  offsetMinutes: number;
  isActive: boolean;
  note: string | null;
}

export interface LadderClass {
  eventClass: string;
  label: string;
  entityType: string;
  dormant: boolean;
}

export const api = {
  login: (email: string, password: string) =>
    request<{ user: SessionUser; entityScope: PillarCode | null }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  logout: async () => {
    try {
      await request<void>('/auth/logout', { method: 'POST' });
    } finally {
      // Preview mode only: drop the carried id so the next login starts clean.
      carriedSession = null;
    }
  },

  /** §4.4 — pending reminder rows, soonest first. */
  upcomingReminders: (opts: { within?: number; projectId?: string; mine?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (opts.within) q.set('within', String(opts.within));
    if (opts.projectId) q.set('projectId', opts.projectId);
    if (opts.mine) q.set('mine', 'true');
    return request<{ window: { days: number }; reminders: UpcomingReminder[] }>(
      `/scheduling/upcoming?${q.toString()}`,
    );
  },

  calendarSyncFailures: () =>
    request<{ count: number; calendarConfigured: boolean; failures: CalendarSyncFailure[] }>(
      '/scheduling/sync-failures',
    ),

  retryCalendarSync: (entityType: string, entityId: string) =>
    request<{ retried: boolean }>('/scheduling/retry-sync', {
      method: 'POST',
      body: JSON.stringify({ entityType, entityId }),
    }),

  reminderLadders: () =>
    request<{ classes: LadderClass[]; rungs: LadderRung[] }>('/scheduling/ladders'),

  updateLadderRung: (id: string, patch: { isActive?: boolean; offsetMinutes?: number }) =>
    request<{ rung: LadderRung; rewritten: { records: number; remindersCreated: number } }>(
      `/scheduling/ladders/${id}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    ),

  me: () => request<{ user: SessionUser; entityScope: PillarCode | null }>('/auth/me'),

  /** §1.4: `?entity=` can only narrow an already-granted scope. */
  setScope: (entityScope: PillarCode | null) =>
    request<{ entityScope: PillarCode | null }>('/auth/scope', {
      method: 'POST',
      // The route reads `entity` (server/src/routes/auth.ts). Sending
      // `entityScope` leaves it undefined, which fails isPillarCode and
      // returns 400 invalid_entity for every switch, including a user who
      // holds all three pillars.
      body: JSON.stringify({ entity: entityScope }),
    }),

  /** Returns `scope`: the granted pillars actually used for the query. */
  projects: (entity?: PillarCode | null) =>
    request<{ projects: Project[]; scope: PillarCode[] }>(
      `/projects${entity ? `?entity=${entity}` : ''}`,
    ),

  project: (id: string) => request<{ project: Project }>(`/projects/${id}`),

  /** The accounts the session may attach a project to, for the form's select. */
  clientAccounts: (entity?: PillarCode | null) =>
    request<{ accounts: ClientAccountOption[]; scope: PillarCode[]; hiddenByScope: number }>(
      `/client-accounts${entity ? `?entity=${entity}` : ''}`,
    ),

  /**
   * Creates a project. The body carries either `clientAccountId` or
   * `newClientAccount` — never both — and the server creates the account and the
   * project in one transaction, so a rejected attempt leaves nothing behind.
   */
  createProject: (body: CreateProjectBody) =>
    request<{ project: Project; createdClientAccount: { id: string; name: string } | null }>(
      '/projects',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /** §4.5 read-only view: the gates standing against the project right now. */
  projectStatus: (id: string) => request<ProjectStatusView>(`/projects/${id}/status`),

  /**
   * P4-8. Manual site coordinate entry (no geocoding provider in the vendor
   * list — paste from Google Maps or a site survey). The server recomputes
   * `weather_group_key` from whatever `siteLat`/`siteLon` land in this call.
   */
  updateProjectSite: (id: string, body: UpdateProjectSiteBody) =>
    request<{ project: ProjectSiteRow }>(`/projects/${id}/site`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  /** P4-8. Cache-only read — never triggers an ad-hoc NWS call. */
  projectWeather: (id: string) => request<ProjectWeather>(`/projects/${id}/weather`),

  /**
   * The only path that changes a status. `reason` carries §4.5's written
   * cause, which is what a hold, a cancellation and a termination all require.
   */
  transitionProject: (id: string, body: { to: string; reason?: string; override?: boolean }) =>
    request<{
      outcome: string;
      project: { id: string; code: string; status: string };
      hold: HoldState;
      gates: GateView[];
      overriddenGates: string[];
    }>(`/projects/${id}/status`, { method: 'POST', body: JSON.stringify(body) }),

  /**
   * The resume date, and a correction to the cause, on a held project. There
   * is no `holdStartedOn` parameter: the start date is the server's to write.
   */
  recordHold: (id: string, body: { resumeOn?: string; holdCause?: string }) =>
    request<{
      project: { id: string; code: string; status: string };
      hold: HoldState;
      exitGates: GateView[];
    }>(`/projects/${id}/hold`, { method: 'PATCH', body: JSON.stringify(body) }),

  /** Deliverable 11: the directory paginates at 50 rows. The client cannot ask for more. */
  contacts: (filters: ContactFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.page && filters.page > 1) params.set('page', String(filters.page));
    if (filters.q) params.set('q', filters.q);
    if (filters.tier) params.set('tier', filters.tier);
    if (filters.category) params.set('category', filters.category);
    const query = params.toString();
    return request<ContactPage>(`/contacts${query ? `?${query}` : ''}`);
  },

  contact: (id: string) =>
    request<{
      contact: ContactDetail;
      projects: { id: string; code: string; name: string; status: string; pillar: PillarCode; role: string }[];
      review: ReviewEntry[];
      merges: MergeRow[];
      relationships: ContactRelationship[];
    }>(`/contacts/${id}`),

  /**
   * P4-2. Links a pair of contacts with no project between them. The server
   * normalises the pair into id order, so the two ids may be sent either way
   * round. `referrerId` is required for 'referred_by' and rejected otherwise.
   */
  addContactRelationship: (body: {
    contactIds: [string, string];
    kind: ContactRelationshipKind;
    referrerId?: string;
    evidence?: string;
    note?: string;
  }) =>
    request<{ relationship: { id: string; kind: ContactRelationshipKind } }>(
      '/contacts/relationships',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  removeContactRelationship: (id: string) =>
    request<void>(`/contacts/relationships/${id}`, { method: 'DELETE' }),

  reviewQueue: async (filters: { page?: number; status?: string; kind?: string } = {}) => {
    const params = new URLSearchParams();
    if (filters.page && filters.page > 1) params.set('page', String(filters.page));
    if (filters.status) params.set('status', filters.status);
    if (filters.kind) params.set('kind', filters.kind);
    const query = params.toString();
    const page = await request<ReviewPage & { entries: Record<string, unknown>[] }>(
      `/contacts/review-queue${query ? `?${query}` : ''}`,
    );
    return { ...page, entries: page.entries.map(nestReviewSides) } as ReviewPage;
  },

  resolveReview: (id: string, action: 'reject' | 'resolve' | 'hold' | 'reopen', note: string) =>
    request<{ entry: { id: string; status: string } }>(`/contacts/review-queue/${id}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ action, note }),
    }),

  /**
   * The only merge path. §3.6: consolidation is an explicit, reviewed, logged
   * operation performed by a named person — never inferred at import.
   */
  mergeContacts: (input: { winnerId: string; loserId: string; reason: string; reviewQueueId?: string }) =>
    request<{ merge: { mergeId: string; fieldsFilledOnWinner: string[]; movedProjectContacts: number } }>(
      '/contacts/merges',
      { method: 'POST', body: JSON.stringify(input) },
    ),

  revertMerge: (mergeId: string, reason: string) =>
    request<{ reverted: { winnerId: string; loserId: string; restoredColumns: string[] } }>(
      `/contacts/merges/${mergeId}/revert`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),

  merges: (page = 1) =>
    request<{ merges: MergeRow[]; page: number; pageSize: number; total: number; hasMore: boolean }>(
      `/contacts/merges${page > 1 ? `?page=${page}` : ''}`,
    ),

  health: () => request<HealthReport>('/health'),

  /* ------------------------------------------ deliverables 15 and 16 ---- */

  /** §1.2.1 Operational Health widget. */
  operationalHealth: (entity?: PillarCode | null) =>
    request<OperationalHealth>(`/dashboard/health${entity ? `?entity=${entity}` : ''}`),

  /** §1.3 pillar buttons — always across every granted pillar, never narrowed. */
  pillarButtons: () =>
    request<{ pillars: PillarButton[]; granted: PillarCode[] }>('/dashboard/pillars'),

  /** §1.4 Company World, all three panels. */
  companyWorld: (entity?: PillarCode | null) =>
    request<CompanyWorld>(`/dashboard/world${entity ? `?entity=${entity}` : ''}`),

  dailyLogs: (entity?: PillarCode | null, days?: number) => {
    const params = new URLSearchParams();
    if (entity) params.set('entity', entity);
    if (days) params.set('days', String(days));
    const query = params.toString();
    return request<DailyLogFeed>(`/dashboard/daily-logs${query ? `?${query}` : ''}`);
  },

  procurement: (entity?: PillarCode | null) =>
    request<ProcurementFeed>(`/dashboard/procurement${entity ? `?entity=${entity}` : ''}`),

  /* ------------------------------------------------ P4-3: documents ---- */

  documents: (entity?: PillarCode | null, category?: string | null) => {
    const params = new URLSearchParams();
    if (entity) params.set('entity', entity);
    if (category) params.set('category', category);
    const query = params.toString();
    return request<DocumentFeed>(`/documents${query ? `?${query}` : ''}`);
  },

  /**
   * §2.4 Flow B. The server hands back a short-lived Dropbox link rather than
   * streaming the file, so the bytes never pass through the platform and the
   * link is fetched at the moment the reader asks for it — not baked into the
   * listing, where it would expire before it was clicked.
   */
  documentLink: (id: string) =>
    request<{ link: string; expiresInSeconds: number }>(`/documents/${id}/link`),

  /**
   * §2.4 Flow C. Fetched as bytes rather than set as an `<img src>`: the
   * endpoint needs the session, and in the hosted preview the session rides a
   * header the browser will not attach to an image request.
   *
   * Returns null when Dropbox will not render the format, which is the
   * caller's signal to draw a glyph instead of a broken image.
   */
  documentThumbnail: async (id: string): Promise<Blob | null> => {
    const carried = readCarriedSession();
    const response = await fetch(`${API_BASE}/api/documents/${id}/thumbnail`, {
      credentials: CREDENTIALS_MODE,
      headers: carried ? { [PREVIEW_SESSION_HEADER]: carried } : {},
    });
    if (!response.ok) return null;
    return response.blob();
  },

  /**
   * §2.4 Flow A. Sent as multipart rather than JSON so the bytes stream
   * through busboy on the server instead of being base64-inflated by a third
   * in transit — a 12 MP phone photo is large enough for that to matter on a
   * jobsite connection.
   *
   * `Content-Type` is deliberately not set: the browser has to supply it
   * itself, because only it knows the multipart boundary it generated.
   */
  uploadDocument: async (input: {
    file: Blob;
    filename: string;
    projectId: string;
    category: string;
    title?: string;
    description?: string;
    tags?: string[];
  }): Promise<UploadResult> => {
    const form = new FormData();
    form.append('projectId', input.projectId);
    form.append('category', input.category);
    if (input.title) form.append('title', input.title);
    if (input.description) form.append('description', input.description);
    if (input.tags?.length) form.append('tags', input.tags.join(','));
    form.append('file', input.file, input.filename);

    const carried = readCarriedSession();
    const response = await fetch(`${API_BASE}/api/documents/upload`, {
      method: 'POST',
      credentials: CREDENTIALS_MODE,
      headers: carried ? { [PREVIEW_SESSION_HEADER]: carried } : {},
      body: form,
    });

    const issued = response.headers.get(PREVIEW_SESSION_HEADER);
    if (issued) storeCarriedSession(issued);

    const text = await response.text();
    const body = text ? (JSON.parse(text) as unknown) : {};
    if (!response.ok) throw new ApiFailure(response.status, body as ApiError);
    return body as UploadResult;
  },

  /* ------------------------------------------------ notifications -- */

  notifications: (opts: { limit?: number; unreadOnly?: boolean } = {}) => {
    const params = new URLSearchParams();
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.unreadOnly) params.set('unreadOnly', 'true');
    const query = params.toString();
    return request<{ notifications: NotificationRow[]; unread: number }>(
      `/notifications${query ? `?${query}` : ''}`,
    );
  },

  unreadCount: () => request<{ unread: number }>('/notifications/unread-count'),

  markNotificationsRead: (ids: string[]) =>
    request<{ updated: number; unread: number }>('/notifications/read', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),

  markAllNotificationsRead: () =>
    request<{ updated: number; unread: number }>('/notifications/read', {
      method: 'POST',
      body: JSON.stringify({ all: true }),
    }),

  pushConfig: () =>
    request<{ configured: boolean; publicKey: string | null }>('/notifications/push/config'),

  pushSubscribe: (subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    request<{ id: string; reassigned: boolean }>('/notifications/push/subscribe', {
      method: 'POST',
      body: JSON.stringify(subscription),
    }),

  pushUnsubscribe: (endpoint: string) =>
    request<{ revoked: boolean }>('/notifications/push/unsubscribe', {
      method: 'POST',
      body: JSON.stringify({ endpoint }),
    }),
};

/* ------------------------------------------------- notifications -- */

export type NotificationKind =
  | 'reminder'
  | 'calendar_sync_failed'
  | 'dropbox_sync_failed'
  | 'weather_alert'
  | 'review_queue'
  | 'system';

export type NotificationSeverity = 'info' | 'warning' | 'critical';

/**
 * `pushState` is surfaced rather than hidden so the interface can tell someone
 * their phone is not actually getting these, instead of letting them believe
 * it is. A person who thinks push is working and is not being notified is
 * worse off than one who knows it is off.
 */
export interface NotificationRow {
  id: string;
  kind: NotificationKind;
  severity: NotificationSeverity;
  title: string;
  body: string | null;
  href: string | null;
  pillar: PillarCode | null;
  projectId: string | null;
  createdAt: string;
  readAt: string | null;
  pushState: 'pending' | 'sent' | 'no_subscription' | 'failed' | 'skipped';
}

/* ------------------------------------------------------- P4-4: capture -- */

/**
 * Why a photo has the capture values it has. Surfaced so the crew can see the
 * difference between "this photo is not proven on site" and "this project has
 * no surveyed point to compare against" — the first is about the photo, the
 * second is a data gap somebody can go fix.
 */
export interface PhotoCapture {
  capturedAt: string | null;
  capturedAtBasis: 'exif_offset' | 'gps_utc' | 'none';
  capturedAtLocalText: string | null;
  gpsLat: number | null;
  gpsLon: number | null;
  make: string | null;
  model: string | null;
  siteVerified: boolean;
  siteBasis: 'within_radius' | 'outside_radius' | 'no_photo_gps' | 'no_site_coordinates';
  siteDistanceMeters: number | null;
  exifSkipReason: string | null;
}

export interface UploadResult {
  id: string;
  projectId: string;
  category: string;
  title: string;
  dropboxPathLower: string;
  sizeBytes: number | null;
  capture: PhotoCapture;
}

/* ------------------------------------------------------ P4-3: documents -- */

/** One row of the §2.4 Flow B listing. */
export interface DocumentSummary {
  id: string;
  projectId: string | null;
  pillar: PillarCode | null;
  category: string;
  title: string;
  description: string | null;
  dropboxPathLower: string;
  sizeBytes: number | null;
  mimeType: string | null;
  uploadedAt: string;
  tags: string[];
  thumbnailAvailable: boolean;
}

export interface DocumentFeed {
  documents: DocumentSummary[];
  count: number;
}

/**
 * A.2.10 `documents.category`, in the order the filter offers them. The server
 * validates against the CHECK constraint regardless of what is sent, so this
 * list decides what the filter offers, never what the database accepts.
 */
export const DOCUMENT_CATEGORIES = [
  'contract',
  'estimate',
  'photo',
  'invoice',
  'addendum',
  'payroll',
  'permit',
  'coi',
  'other',
  'uncategorized',
] as const;
export type DocumentCategory = (typeof DOCUMENT_CATEGORIES)[number];

export const DOCUMENT_CATEGORY_LABELS: Record<DocumentCategory, string> = {
  contract: 'Contract & award',
  estimate: 'Estimate & budget',
  photo: 'Progress photo',
  invoice: 'Invoice & pay app',
  addendum: 'Addendum',
  payroll: 'Payroll',
  permit: 'Permit',
  coi: 'Certificate of insurance',
  other: 'Other',
  uncategorized: 'Uncategorized',
};

export interface HealthReport {
  status: 'ok' | 'degraded' | 'fail';
  version: string;
  uptimeSeconds: number;
  timestamp: string;
  checks: Record<string, { status: string; [key: string]: unknown }>;
}

/**
 * Changes on every rebuild so the hosted preview's asset filenames change too.
 * A reviewer whose browser cached an earlier bundle would otherwise keep running
 * it after a fix ships, and report the old behaviour as still broken.
 */
export const BUILD_STAMP = '2026-09-09T19:50Z';
